import React, { useState, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TagCombobox } from '@/components/TagCombobox';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { money } from '@/lib/helpers';
import { CustomFieldsForm } from '@/components/CustomFieldsForm';
import { validateCustom, cleanCustomValues, fieldsFor } from '@/lib/customFields';
import InlineScanner from '@/components/InlineScanner';
import { unlockAudio } from '@/lib/scanFeedback';
import { Plus, Trash2, Upload, LayoutGrid, X, ScanLine } from 'lucide-react';

/**
 * Manages a list of garments with per-item store allocation + bulk tools + CSV/Excel import.
 * garments: array of {barcode, product, category, size, colour, price, cost, store_id}
 */
export default function GarmentAllocator({ garments, setGarments, defaultCategory = '' }) {
  const { stores, settings } = useApp();
  const customDefs = settings?.custom_fields || [];
  const invFields = fieldsFor(customDefs, 'inventory');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [scanActive, setScanActive] = useState(false);
  const [customErrs, setCustomErrs] = useState({});
  const [selected, setSelected] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importStore, setImportStore] = useState(stores[0]?.id || '');
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  function blank() {
    return { barcode: '', product: '', category: defaultCategory || '', size: '', colour: '', price: '', cost: '', store_id: stores[0]?.id || '', custom: {} };
  }

  // Selecting a category auto-suggests the next design code (e.g. after 1087A/B -> 1088A).
  const pickCategory = async (v) => {
    setForm((f) => ({ ...f, category: v }));
    const cat = (v || '').trim();
    if (!cat) return;
    try {
      const info = (await api.get('/catalog/next-code', { params: { category: cat } })).data.data;
      if (info && info.next_code) setForm((f) => (f.barcode.trim() ? f : { ...f, barcode: info.next_code }));
    } catch (e) { /* ignore — suggestion is best-effort */ }
  };

  const addManual = async () => {
    if (!form.barcode.trim()) { toast.error('Enter a garment code/barcode.'); return; }
    if (!form.price) { toast.error('Enter a selling price.'); return; }
    if (!form.cost) { toast.error('Enter the item cost (purchase price). Stock value is calculated on cost.'); return; }
    const bc = form.barcode.trim().toUpperCase();
    if (garments.some((g) => g.barcode === bc)) { toast.error('This code is already in the list.'); return; }
    try {
      const ex = (await api.post('/garments/check-barcodes', { barcodes: [bc] })).data.data.existing || {};
      if (ex[bc]) {
        const e = ex[bc];
        toast.error(`Barcode already exists: ${e.product || ''} · ${e.status} · ${e.store_name || ''}`);
        return;
      }
    } catch (err) { /* offline: allow, server will still enforce uniqueness */ }
    const cErrs = validateCustom(customDefs, 'inventory', form.custom);
    if (Object.keys(cErrs).length) { setCustomErrs(cErrs); toast.error('Please fill the required custom fields.'); return; }
    setCustomErrs({});
    setGarments((g) => [...g, { ...form, barcode: bc, price: Number(form.price), cost: Number(form.cost), store_id: form.store_id || stores[0]?.id, custom: cleanCustomValues(customDefs, 'inventory', form.custom) }]);
    setForm({ ...blank(), product: form.product, category: form.category, size: form.size, colour: form.colour, price: form.price });
    setAddOpen(false);
    toast.success('Garment added');
  };

  const remove = (bc) => setGarments((g) => g.filter((x) => x.barcode !== bc));
  const setRowStore = (bc, sid) => setGarments((g) => g.map((x) => (x.barcode === bc ? { ...x, store_id: sid } : x)));

  const allTo = (sid) => { setGarments((g) => g.map((x) => ({ ...x, store_id: sid }))); toast.success('Allocated all'); };
  const selectedTo = (sid) => {
    setGarments((g) => g.map((x) => (selected.includes(x.barcode) ? { ...x, store_id: sid } : x)));
    setSelected([]); toast.success('Allocated selected');
  };
  const toggleSel = (bc) => setSelected((s) => (s.includes(bc) ? s.filter((x) => x !== bc) : [...s, bc]));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/stock/parse-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const rows = r.data.data.rows;
      const cols = r.data.data.columns;
      if (!rows.length) { toast.error('The file is empty.'); return; }
      setImportRows(rows);
      const guess = (names) => cols.find((c) => names.some((n) => c.toLowerCase().includes(n)));
      setMapping({
        barcode: guess(['barcode', 'code', 'sku']) || cols[0],
        product: guess(['product', 'design', 'name', 'item']) || cols[1] || cols[0],
        size: guess(['size']) || '',
        colour: guess(['colour', 'color']) || '',
        category: guess(['category', 'cat']) || '',
        price: guess(['price', 'mrp', 'rate']) || '',
        cost: guess(['cost']) || '',
      });
      setImportStore(stores[0]?.id || '');
      setPreview(null);
      setImportOpen(true);
    } catch (err) { toast.error(errMsg(err)); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const confirmImport = () => {
    doImport(preview.validRows);
  };

  const buildRows = () => {
    const seen = new Set();
    const parsed = [];
    for (const row of importRows) {
      const bc = String(row[mapping.barcode] ?? '').trim().toUpperCase();
      const product = String(row[mapping.product] ?? '').trim();
      const priceRaw = mapping.price ? String(row[mapping.price] ?? '') : '';
      const price = priceRaw ? Number(priceRaw.replace(/[^0-9.]/g, '')) || 0 : 0;
      parsed.push({
        barcode: bc, product,
        category: (mapping.category ? String(row[mapping.category] ?? '').trim() : '') || defaultCategory,
        size: mapping.size ? String(row[mapping.size] ?? '').trim() : '',
        colour: mapping.colour ? String(row[mapping.colour] ?? '').trim() : '',
        price, cost: mapping.cost ? (Number(String(row[mapping.cost]).replace(/[^0-9.]/g, '')) || null) : null,
        store_id: importStore,
        _dupInFile: bc ? seen.has(bc) : false,
      });
      if (bc) seen.add(bc);
    }
    return parsed;
  };

  const computePreview = async () => {
    const parsed = buildRows();
    const codes = parsed.filter((r) => r.barcode).map((r) => r.barcode);
    let existing = {};
    try {
      existing = (await api.post('/garments/check-barcodes', { barcodes: codes })).data.data.existing || {};
    } catch (e) { /* offline: skip server dup check */ }
    const missing = [], duplicates = [], validRows = [];
    for (const r of parsed) {
      if (!r.barcode || !r.price || r.cost == null) { missing.push(r); continue; }
      if (r._dupInFile || existing[r.barcode] || garments.some((g) => g.barcode === r.barcode)) { duplicates.push(r); continue; }
      validRows.push({ ...r, _dupInFile: undefined });
    }
    setPreview({ total: parsed.length, valid: validRows.length, duplicates: duplicates.length, missing: missing.length, validRows });
  };

  const doImport = (rows) => {
    setGarments((g) => [...g, ...rows]);
    setImportOpen(false);
    setPreview(null);
    toast.success(`${rows.length} garments imported`);
  };

  const counts = stores.map((s) => ({ ...s, count: garments.filter((g) => g.store_id === s.id).length }));
  const unalloc = garments.filter((g) => !g.store_id).length;

  return (
    <div>
      <div className="flex gap-2">
        <Button data-testid="allocator-add-manual" variant="secondary" className="h-11 flex-1 rounded-xl" onClick={() => { setForm(blank()); setAddOpen(true); }}>
          <Plus className="mr-1 h-4 w-4" /> Add garment
        </Button>
        <Button data-testid="allocator-import" variant="secondary" className="h-11 flex-1 rounded-xl" onClick={() => fileRef.current?.click()}>
          <Upload className="mr-1 h-4 w-4" /> Import CSV/Excel
        </Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="allocator-file-input" />
      </div>

      {garments.length > 0 && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3 text-xs">
            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Total {garments.length}</span>
            {counts.map((c) => (<span key={c.id} className="rounded-full bg-secondary px-2 py-0.5">{c.name}: {c.count}</span>))}
            {unalloc > 0 && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-700">Unallocated: {unalloc}</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {stores.map((s) => (
              <Button key={s.id} size="sm" variant="outline" className="h-8 rounded-full text-xs" data-testid={`allocator-all-${s.id}`} onClick={() => allTo(s.id)}>All &rarr; {s.name}</Button>
            ))}
            {selected.length > 0 && stores.map((s) => (
              <Button key={'sel' + s.id} size="sm" className="h-8 rounded-full text-xs" data-testid={`allocator-selected-${s.id}`} onClick={() => selectedTo(s.id)}>{selected.length} selected &rarr; {s.name}</Button>
            ))}
          </div>

          <div className="mt-3 space-y-2">
            {garments.map((g) => (
              <div key={g.barcode} data-testid={`allocator-row-${g.barcode}`} className="flex items-center gap-2 rounded-xl border border-border bg-card p-2.5">
                <input type="checkbox" checked={selected.includes(g.barcode)} onChange={() => toggleSel(g.barcode)}
                  className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid={`allocator-select-${g.barcode}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{g.product || '(no name)'}</p>
                  <p className="font-mono text-xs text-muted-foreground">{g.barcode} · {money(g.price)}</p>
                </div>
                <Select value={g.store_id} onValueChange={(v) => setRowStore(g.barcode, v)}>
                  <SelectTrigger className="h-9 w-[104px] rounded-lg text-xs" data-testid={`allocator-store-${g.barcode}`}><SelectValue placeholder="Store" /></SelectTrigger>
                  <SelectContent>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                </Select>
                <button onClick={() => remove(g.barcode)} data-testid={`allocator-remove-${g.barcode}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add manual drawer */}
      <Drawer open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setScanActive(false); }}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Add garment</DrawerTitle></DrawerHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto px-4 pb-6">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Field label="Code / Barcode *">
                  <div className="flex gap-2">
                    <Input data-testid="allocator-form-barcode" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="h-11 flex-1 rounded-xl uppercase" />
                    <Button type="button" variant={scanActive ? 'default' : 'secondary'} className="h-11 w-11 shrink-0 rounded-xl p-0"
                      onClick={() => { unlockAudio(); setScanActive((v) => !v); }} data-testid="allocator-scan-toggle"
                      aria-pressed={scanActive} aria-label={scanActive ? 'Close scanner' : 'Scan barcode'}>
                      {scanActive ? <X className="h-4 w-4" /> : <ScanLine className="h-4 w-4" />}
                    </Button>
                  </div>
                </Field>
                {scanActive && (
                  <InlineScanner
                    onDetected={(code) => { setForm((f) => ({ ...f, barcode: code })); setScanActive(false); toast.success(`Scanned ${code}`); }}
                    onClose={() => setScanActive(false)}
                  />
                )}
              </div>
              <Field label="Selling price *"><Input inputMode="numeric" data-testid="allocator-form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-11 rounded-xl" /></Field>
              <Field label="Garment name"><Input data-testid="allocator-form-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="h-11 rounded-xl" /></Field>
              <Field label="Category"><TagCombobox value={form.category} onChange={pickCategory} placeholder="Select / create" testId="allocator-form-category" filterKind="category" createExtra={{ ensure_supplier: false }} /></Field>
              <Field label="Size"><Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="h-11 rounded-xl" /></Field>
              <Field label="Colour"><Input value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} className="h-11 rounded-xl" /></Field>
              <Field label="Cost (Purchase price) *"><Input inputMode="numeric" data-testid="allocator-form-cost" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="h-11 rounded-xl" /></Field>
              <Field label="Store">
                <Select value={form.store_id} onValueChange={(v) => setForm({ ...form, store_id: v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Store" /></SelectTrigger>
                  <SelectContent>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                </Select>
              </Field>
            </div>
            {invFields.length > 0 && (
              <div className="rounded-xl border border-border p-3" data-testid="allocator-custom-fields">
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Additional details</p>
                <CustomFieldsForm defs={customDefs} context="inventory" value={form.custom || {}}
                  onChange={(v) => setForm({ ...form, custom: v })} errors={customErrs} idPrefix="allocator-cf" />
              </div>
            )}
            <Button data-testid="allocator-form-save" className="h-12 w-full rounded-xl" onClick={addManual}>Add to list</Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Import mapping drawer */}
      <Drawer open={importOpen} onOpenChange={setImportOpen}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Map columns ({importRows.length} rows)</DrawerTitle></DrawerHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto px-4 pb-6">
            {['barcode', 'product', 'category', 'size', 'colour', 'price', 'cost'].map((field) => (
              <div key={field} className="flex items-center justify-between gap-2">
                <span className="w-24 text-sm capitalize">{field}{(field === 'barcode' || field === 'cost') ? ' *' : ''}</span>
                <Select value={mapping[field] || 'none'} onValueChange={(v) => { setMapping({ ...mapping, [field]: v === 'none' ? '' : v }); setPreview(null); }}>
                  <SelectTrigger className="h-10 flex-1 rounded-xl text-sm" data-testid={`import-map-${field}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not mapped</SelectItem>
                    {Object.keys(importRows[0] || {}).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            <Field label="Allocate all imported to">
              <Select value={importStore} onValueChange={(v) => { setImportStore(v); setPreview(null); }}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="import-store"><SelectValue /></SelectTrigger>
                <SelectContent>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
              </Select>
            </Field>
            {!preview ? (
              <Button data-testid="import-preview" className="h-12 w-full rounded-xl" onClick={computePreview}>Preview import</Button>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="import-preview-summary">
                  <div className="flex justify-between"><span className="text-muted-foreground">Total rows</span><span className="font-semibold">{preview.total}</span></div>
                  <div className="flex justify-between"><span className="text-emerald-700">Valid</span><span className="font-semibold text-emerald-700">{preview.valid}</span></div>
                  <div className="flex justify-between"><span className="text-rose-700">Duplicates</span><span className="font-semibold text-rose-700">{preview.duplicates}</span></div>
                  <div className="flex justify-between"><span className="text-amber-700">Missing info</span><span className="font-semibold text-amber-700">{preview.missing}</span></div>
                </div>
                {(preview.duplicates > 0 || preview.missing > 0) && (
                  <p className="text-xs text-muted-foreground">Only the {preview.valid} valid rows will be imported. Duplicate barcodes and rows missing a code, price or cost are skipped.</p>
                )}
                <div className="flex gap-2">
                  <Button variant="secondary" className="h-12 flex-1 rounded-xl" onClick={() => setPreview(null)}>Re-map</Button>
                  <Button data-testid="import-confirm" className="h-12 flex-1 rounded-xl" disabled={preview.valid === 0} onClick={confirmImport}>Import {preview.valid} valid</Button>
                </div>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);
